import { Navigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { GateWaitNotice } from "@/components/auth/GateWaitNotice";
import { QueryErrorNotice } from "@/components/QueryErrorNotice";
import type { OnboardingGateOutcome } from "@/lib/onboarding";

/**
 * The one element the gate renders, for every state it can be in.
 *
 * Pulled out as a pure factory, and kept to a single `AppLayout` element across
 * both terminal states, because the alternative is what the QA marathon's
 * second baseline measured: a load of /payment on which the layout mounted
 * twenty-five times and the page showed nothing. In run
 * 20260909T000907955Z-23e3, `/api/dashboard/alerts/`, `/api/banking/accounts/`
 * and `/api/payments/status/` each fired twenty-five times on that one load and
 * `/api/profile/me/` fifty-one. Those queries belong to components inside the
 * layout, so the repeat count IS the mount count.
 *
 * React reconciles by element type at a position. While the gate answered a
 * failed profile with a `div` and a loaded one with `AppLayout`, every change
 * of query state threw the shell away and built a new one, whose queries all
 * fetched again, some of which failed again. Answering both with `AppLayout`
 * makes those two the same mounted layout showing different content, so a
 * failure that never clears settles instead of looping, and a retry that
 * succeeds swaps the notice for the page without rebuilding the shell around
 * it.
 *
 * BEN-141 QF8.
 */
export function onboardingGateElement(
  outcome: OnboardingGateOutcome,
  failure: { error: unknown; onRetry: () => void },
): React.ReactElement | null {
  switch (outcome) {
    // No answer yet. Still nothing for the first few seconds, still on purpose:
    // a flash of chrome ahead of a redirect into the wizard reads worse than a
    // blank frame, and it is one mount rather than a loop. After
    // SESSION_BOOTSTRAP_TIMEOUT_MS it stops being a wait and says so, because a
    // pending request has no upper bound and neither did this (#1201).
    case "wait":
      return <GateWaitNotice onRetry={failure.onRetry} />;
    case "onboarding":
      return <Navigate to="/onboarding" replace />;
    case "failure":
      // The shell, with the failure in the content column. The sidebar, the
      // header and the profile menu are all reachable, so an account that
      // cannot be read is not also a dead end: the user can try again, move to
      // another route, or sign out. The page itself stays closed, because the
      // gate has not established that this account finished onboarding.
      return (
        <AppLayout>
          <QueryErrorNotice error={failure.error} area="your account" onRetry={failure.onRetry} />
        </AppLayout>
      );
    case "app":
      return <AppLayout />;
  }
}
